import React, { useState, useMemo } from 'react';
import { Box, Typography, TextField, InputAdornment, Accordion, AccordionSummary, AccordionDetails, Card, CardMedia, CardContent, Grid, Dialog, IconButton, Fade } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import tutorialData from '../../utils/TutorialsData';

const HelpSupport = () => {
  const [search, setSearch] = useState('');
  const [selectedVideo, setSelectedVideo] = useState(null);

  const filteredData = useMemo(() => {
    if (!search) return tutorialData;
    return tutorialData
      .map(section => ({
        ...section,
        videos: section.videos.filter(v =>
          v.title.toLowerCase().includes(search.toLowerCase())
        )
      }))
      .filter(section => section.videos.length > 0);
  }, [search]);

  const renderCard = (video, chapterTitle = '') => (
    <Grid item xs={12} sm={6} md={4} key={video.videoId}>
      <Card
        onClick={() => setSelectedVideo(video)}
        sx={{
          cursor: 'pointer',
          borderRadius: 3,
          overflow: 'hidden',
          boxShadow: 3,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'scale(1.02)',
            boxShadow: 6,
          },
          position: 'relative'
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component="img"
            image={video.thumbnail}
            alt={video.title}
            style={{ objectFit: 'cover' }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              borderRadius: '50%',
              padding: '1px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <PlayCircleOutlineIcon
              sx={{
                fontSize: 48,
                color: '#fff'
              }}
            />
          </Box>
        </Box>
        <CardContent sx={{ backgroundColor: '#fff' }}>
          <Typography variant="body1" fontWeight={600}>{video.title}</Typography>
          {chapterTitle && (
            <Typography variant="caption" color="text.secondary">
              {chapterTitle}
            </Typography>
          )}
        </CardContent>
      </Card>
    </Grid>
  );

  return (
    <Box sx={{ padding: { xs: '80px 20px 30px', md: '120px 40px 40px' }, backgroundColor: '#F6F6F6', minHeight: '100vh' }}>
      <Typography variant="h4" sx={{ marginBottom: 2, color: '#003049', fontWeight: 700 }}>
        POS Video Tutorials
      </Typography>
      <Typography variant="subtitle1" sx={{ marginBottom: 3, color: '#555' }}>
        Learn how to make the most of your system, one video at a time.
      </Typography>

      <TextField
        fullWidth
        placeholder="Search tutorials..."
        variant="outlined"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: '#1D7454' }} />
            </InputAdornment>
          ),
        }}
        sx={{
          marginBottom: 4,
          backgroundColor: '#fff',
          borderRadius: 2,
          boxShadow: 1
        }}
      />

      {search ? (
        filteredData.length === 0 ? (
          <Typography>No matching tutorials found.</Typography>
        ) : (
          <Grid container spacing={3}>
            {filteredData.flatMap(chapter =>
              chapter.videos.map(video => renderCard(video, chapter.chapter))
            )}
          </Grid>
        )
      ) : (
        filteredData.map((chapter, idx) => (
          <Accordion key={idx} sx={{ marginBottom: 2, borderRadius: 2, boxShadow: 1 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: '#E3EEF3 ' }}>
              <Typography variant="h6" sx={{ color: '#003049', fontWeight: 600 }}>
                {chapter.chapter}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingTop: 4, paddingBottom: 4 }}>
              <Grid container spacing={3}>
                {chapter.videos.map(video => renderCard(video))}
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))
      )}

      <Dialog
        open={!!selectedVideo}
        onClose={() => setSelectedVideo(null)}
        maxWidth="md"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none'
          }
        }}
      >
        <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
          <iframe
            src={`${selectedVideo?.videoId}`}
            title={selectedVideo?.title}
            frameBorder="0"
            allowFullScreen
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              borderRadius: 8,
              boxShadow: '0 4px 12px rgba(0,0,0,0.5)'
            }}
          />
          <IconButton
            onClick={() => setSelectedVideo(null)}
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              color: '#fff',
              backgroundColor: 'rgba(0,0,0,0.5)',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </Dialog>
    </Box>
  );
};

export default HelpSupport;
